import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/Card';
import { Button } from '@/components/Button';
import { Combobox } from '@/components/Combobox';
import { Skeleton } from '@/components/EmptyState';
import { api } from '@/lib/api';
import { toast } from '@/components/Toaster';

interface Quarantine { id: string; original_value: string; source_table: string; source_id: string; }
interface UOM        { id: string; name: string; code: string; }

/**
 * UOM quarantine resolver — surfaces UOM values from imported / external rows
 * that didn't match a known unit. Each quarantine row is mapped to a canonical
 * UOM so future rows with the same string land cleanly.
 */
export function UOMQuarantine() {
  const qc = useQueryClient();
  const { data: qResp, isLoading } = useQuery({
    queryKey: ['uom-quarantine'],
    queryFn:  () => api<{ items: Quarantine[] }>('/stock/uoms/quarantine'),
  });
  const { data: uResp } = useQuery({
    queryKey: ['uoms'],
    queryFn:  () => api<{ items: UOM[] }>('/stock/uoms'),
  });
  const quarantines = qResp?.items ?? [];
  const uoms        = uResp?.items ?? [];

  const [picks, setPicks] = useState<Record<string, string | null>>({});

  const uomOptions = useMemo(
    () => uoms.map((u) => ({ value: u.id, label: u.name, description: u.code })),
    [uoms],
  );

  const resolveMutation = useMutation({
    mutationFn: (input: { quarantine_id: string; uom_id: string }) =>
      api<{ ok: boolean }>('/stock/uoms/quarantine/resolve', { method: 'POST', body: input }),
    onSuccess: () => {
      toast.success('Resolved', 'Quarantined row mapped to canonical UOM.');
      qc.invalidateQueries({ queryKey: ['uom-quarantine'] });
    },
    onError: (e: { message?: string }) => toast.error('Resolve failed', e?.message ?? 'See API logs'),
  });

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Inventory', to: '/stock' },
          { label: 'UOMs', to: '/stock/uoms' },
          { label: 'Quarantine' },
        ]}
        title="UOM Quarantine"
        subtitle="Imported or external rows whose UOM string didn't match a known unit. Map each to a canonical UOM to clear the queue."
        actions={
          <Button variant="ghost" asChild>
            <Link to={'/stock/uoms' as never}><ArrowLeft className="size-4" /> Back to UOMs</Link>
          </Button>
        }
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-4xl">
        {isLoading ? (
          <Card><Skeleton className="h-32 w-full" /></Card>
        ) : quarantines.length === 0 ? (
          <Card className="!p-8 text-center">
            <CheckCircle2 className="size-8 text-brand-green mx-auto" />
            <CardTitle>Inbox clear</CardTitle>
            <CardDescription>No unresolved UOM quarantine entries.</CardDescription>
          </Card>
        ) : (
          <Card padded={false}>
            <div className="px-5 py-3 border-b border-hairline flex items-center gap-2">
              <AlertTriangle className="size-4 text-warning" />
              <CardTitle>{quarantines.length} unresolved {quarantines.length === 1 ? 'entry' : 'entries'}</CardTitle>
            </div>
            <table className="w-full text-body-sm">
              <thead className="bg-surface-soft text-micro-uppercase text-stone">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Original value</th>
                  <th className="text-left font-medium px-4 py-2">Source</th>
                  <th className="text-left font-medium px-4 py-2 min-w-[240px]">Map to canonical UOM</th>
                  <th className="px-4 py-2 w-32" />
                </tr>
              </thead>
              <tbody>
                {quarantines.map((q) => (
                  <tr key={q.id} className="border-t border-hairline">
                    <td className="px-4 py-2 font-mono text-caption">{q.original_value}</td>
                    <td className="px-4 py-2 text-stone">
                      <span className="text-charcoal">{q.source_table}</span>
                      <span className="text-stone"> · </span>
                      <span className="font-mono text-caption">{q.source_id.slice(-8)}</span>
                    </td>
                    <td className="px-4 py-2">
                      <Combobox
                        options={uomOptions}
                        value={picks[q.id] ?? null}
                        onChange={(v) => setPicks((p) => ({ ...p, [q.id]: v }))}
                        placeholder="Pick UOM…"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          const uomID = picks[q.id];
                          if (!uomID) { toast.error('Pick a UOM first'); return; }
                          resolveMutation.mutate({ quarantine_id: q.id, uom_id: uomID });
                        }}
                        loading={resolveMutation.isPending}
                      >
                        Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </>
  );
}
